import React, { useEffect, useState, useCallback, useRef } from "react";
import { ProjectList } from "../../Api/Project";
import { defaultPage } from "../../Common/Constant";
import Footer from "../Layout/Footer";
import Header from "../Layout/Header";
import Pagination from "../Layout/Pagination";
import Preloader from "../Layout/Preloader";
import { NavLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { CModal, CModalHeader, CModalTitle, CModalBody } from "@coreui/react";
import moment from "moment";
import { ProjecGroupDetail } from "../../Api/ProjectGroup";

const VALID_STATUS = ["active", "inactive", "invoiced", "closed", "total"];

function All_project() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchSeq = useRef(0);

  useEffect(() => {
    document.title = "All Project - Mindforce";
  }, []);

  const statusParam = searchParams.get("status");
  const status = VALID_STATUS.includes(statusParam) ? statusParam : "active";
  const q = searchParams.get("q") || "";
  const pageRaw = parseInt(searchParams.get("page") || "1", 10);
  const page = !isNaN(pageRaw) && pageRaw > 0 ? pageRaw : 1;

  const [searchInput, setSearchInput] = useState(q);
  const [lists, setLists] = useState();
  const [dataCount, setDataCount] = useState();
  const [pageInfo, setPageInfo] = useState({});
  const [childRow, setchildRow] = useState(false);
  const [parentId, setParentId] = useState();
  const [detail, setDetail] = useState();
  const [redirectLinks, setRedirectLinks] = useState();
  const [visibleDetail, setVisibleDetail] = useState(false);
  const [collapsed] = useState(false);
  const [hoveredRow, setHoveredRow] = useState(-1);

  // Canonicalize missing/invalid URL params
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    let changed = false;
    if (!VALID_STATUS.includes(statusParam)) {
      next.set("status", "active");
      changed = true;
    }
    const pageStr = searchParams.get("page");
    if (pageStr != null) {
      const p = parseInt(pageStr, 10);
      if (isNaN(p) || p < 1) {
        next.delete("page");
        changed = true;
      } else if (p === 1) {
        next.delete("page");
        changed = true;
      }
    }
    if (!searchParams.get("q")) {
      // leave q absent when empty
    } else if ((searchParams.get("q") || "").trim() === "") {
      next.delete("q");
      changed = true;
    }
    if (changed) {
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, statusParam, setSearchParams]);

  useEffect(() => {
    setSearchInput(q);
  }, [q]);

  // Home links use /projects?status=active. Also clear an unapplied search
  // draft when the user clicks Home while already on that exact URL.
  useEffect(() => {
    if (
      location.pathname === "/projects" &&
      status === "active" &&
      !q &&
      page === 1
    ) {
      setSearchInput("");
    }
  }, [location.key, location.pathname, status, q, page]);

  const updateParams = useCallback((updates) => {
    const next = new URLSearchParams(searchParams);
    Object.keys(updates).forEach((key) => {
      const value = updates[key];
      if (value === null || value === undefined || value === "") {
        next.delete(key);
      } else {
        next.set(key, String(value));
      }
    });
    if (next.get("page") === "1") next.delete("page");
    if (!(next.get("q") || "").trim()) next.delete("q");
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  const getList = useCallback(async () => {
    const seq = ++fetchSeq.current;
    setLists(undefined);
    // Search is status-independent (matches previous behavior): when q is set,
    // do not apply the status filter. Status filter applies only when not searching.
    const hasSearch = !!(q && q.trim());
    const filter = hasSearch
      ? []
      : status === "total"
        ? []
        : [{ field: "status", value: status }];
    let res = await ProjectList({
      page,
      recPerPage: defaultPage.perPage,
      searchKeyword: q,
      filter,
    });
    if (seq !== fetchSeq.current) return;
    if (res.status === 1) {
      setLists(res.payload);
      setDataCount(res.dataCount);
      setPageInfo(res.paginationInfo);
    } else {
      setLists([]);
      setDataCount(res.dataCount || {});
      setPageInfo({});
    }
  }, [status, q, page]);

  useEffect(() => {
    getList();
  }, [getList]);

  const handleStatus = (value) => {
    setSearchInput("");
    setchildRow(false);
    setParentId(undefined);
    updateParams({ status: value, q: null, page: null });
  };

  const applySearch = () => {
    setchildRow(false);
    setParentId(undefined);
    updateParams({ q: (searchInput || "").trim() || null, page: null });
  };

  const clearSearch = () => {
    setSearchInput("");
    setchildRow(false);
    setParentId(undefined);
    updateParams({ q: null, page: null });
  };

  const changePage = (p) => {
    if (!p) return;
    setchildRow(false);
    setParentId(undefined);
    updateParams({ page: p === 1 ? null : p });
  };

  const projectDetailGroup = async (id) => {
    let res = await ProjecGroupDetail({ id: id });
    if (res.status === 1) {
      setDetail(res.payload[0]);
      setRedirectLinks(res?.redirectLinks);
      setVisibleDetail(true);
    }
  };

  const expdRow = (projectId) => {
    if (childRow && parentId === projectId) {
      setchildRow(false);
      setParentId(undefined);
      return;
    }
    setParentId(projectId);
    setchildRow(true);
  };

  // Function to handle mouse hover
  const handleMouseEnter = (ID) => setHoveredRow(ID);
  const handleMouseLeave = () => setHoveredRow(-1);

  const rowOffset = (page - 1) * (pageInfo?.perPage || defaultPage.perPage);

  return (
    <div>
      <Header />
      <div id="layout-wrapper">
        <div className={`main-content ${collapsed ? "collapsed" : ""}`}>
          <div className="page-content">
            <div className="container-fluid ">
              <div className="row">
                <div className="col-12 col-lg-12">
                  <div
                    className="page-title-box d-sm-flex justify-content-between"
                    style={{ alignItems: "flex-end" }}
                  >
                    <div>
                      <h4 className="mb-sm-0">Projects</h4>
                      <div
                        className="page-title-right"
                        style={{ fontSize: "10px" }}
                      >
                        <ol className="breadcrumb m-0">
                          <li className="breadcrumb-item">
                            <a> Project</a>
                          </li>
                          {/* <li className="breadcrumb-item active">
                            all
                          </li> */}
                        </ol>
                      </div>
                    </div>
                    <div className="button-parent">
                      <div className="client_active_btn d-flex ">
                        

                        <input
                          type="radio"
                          className="btn-check "
                          name="status"
                          id="option2"
                          value="active"
                          checked={status === "active"}
                          onChange={() => handleStatus("active")}
                          autocomplete="off"
                        />
                        <div>
                          {" "}
                          <label
                            className="btn btn-white btn-sm "
                            htmlFor="option2"
                            style={{
                              backgroundColor:
                                status === "active"
                                  ? "#979292"
                                  : "white",
                              color:
                                status === "active"
                                  ? "white"
                                  : "black",
                            }}
                          >
                            Active{" "}
                          </label>
                          <label
                            htmlFor="option2"
                            className="btn btn-color1 btn-sm"
                          >
                            {dataCount ? dataCount?.active : 0}
                          </label>
                        </div>

                        <input
                          type="radio"
                          className="btn-check"
                          name="status"
                          id="option3"
                          value="inactive"
                          checked={status === "inactive"}
                          onChange={() => handleStatus("inactive")}
                          autocomplete="off"
                        />
                        <div>
                          {" "}
                          <label
                            className="btn btn-white btn-sm"
                            htmlFor="option3"
                            style={{
                              backgroundColor:
                                status === "inactive"
                                  ? "#979292"
                                  : "white",
                              color:
                                status === "inactive"
                                  ? "white"
                                  : "black",
                            }}
                          >
                            In Active{" "}
                          </label>
                          <label
                            htmlFor="option3"
                            className="btn btn-color2 btn-sm"
                          >
                            {dataCount ? dataCount?.inactive : 0}
                          </label>
                        </div>

                        <input type="radio"
                          className="btn-check"
                          name="status"
                          id="option4"
                          value="invoiced"
                          checked={status === "invoiced"}
                          onChange={() => handleStatus("invoiced")}
                          autocomplete="off"
                        />
                        <div>
                          {" "}
                          <label
                            className="btn btn-white btn-sm"
                            htmlFor="option4"
                            style={{
                              backgroundColor:
                                status === "invoiced"
                                  ? "#979292"
                                  : "white",
                              color:
                                status === "invoiced"
                                  ? "white"
                                  : "black",
                            }}
                          >
                            Invoiced{" "}
                          </label>
                          <label
                            htmlFor="option4"
                            className="btn btn-color3 btn-sm"
                          >
                            {dataCount ? dataCount?.invoiced : 0}
                          </label>
                        </div>

                        <input
                          type="radio"
                          className="btn-check"
                          name="status"
                          id="option5"
                          value="closed"
                          checked={status === "closed"}
                          onChange={() => handleStatus("closed")}
                          autocomplete="off"
                        />
                        <div>
                          {" "}
                          <label
                            className="btn btn-white btn-sm"
                            htmlFor="option5"
                            style={{
                              backgroundColor:
                                status === "closed"
                                  ? "#979292"
                                  : "white",
                              color:
                                status === "closed"
                                  ? "white"
                                  : "black",
                            }}
                          >
                            Closed{" "}
                          </label>
                          <label
                            htmlFor="option5"
                            className="btn btn-color4 btn-sm"
                          >
                            {dataCount ? dataCount?.closed : 0}
                          </label>
                        </div>

                        <input
                          type="radio"
                          className="btn-check"
                          name="status"
                          id="option1"
                          value="total"
                          checked={status === "total"}
                          onChange={() => handleStatus("total")}
                          autocomplete="off"
                        />
                        <div>
                          {" "}
                          <label
                            className="btn btn-white btn-sm"
                            htmlFor="option1"
                            style={{
                              backgroundColor:
                                status === "total"
                                  ? "#979292"
                                  : "white",
                              color:
                                status === "total"
                                  ? "white"
                                  : "black",
                            }}
                          >
                            Total
                          </label>
                          <label
                            htmlFor="option1"
                            className="btn btn-color btn-sm "
                          >
                            {dataCount ? dataCount?.total : 0}
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="search" style={{ display: "flex" }}>
                      <input
                        style={{ borderRadius: "5px 0 0 5px" }}
                        type="text"
                        className="form-control"
                        placeholder="Search..."
                        autocomplete="off"
                        id="search-options"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            applySearch();
                          }
                        }}
                      />
                      <button
                        style={{ borderRadius: "0 5px 5px 0" }}
                        type="button"
                        className="btn btn-primary"
                        onClick={() => {
                          applySearch();
                        }}
                      >
                        <span className="mdi mdi-magnify search-widget-icon"></span>
                      </button>
                      {(searchInput || q) && (
                        <button
                          type="button"
                          className="btn btn-danger ms-3"
                          onClick={clearSearch}
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {/* <!-- end page title --> */}

              <div className="row mt-2">
                <div className="col-xl-12 col-lg-12">
                  <div className="card">
                    {/* <!-- end card header --> */}

                    <div className="card-body" style={{ padding: "10px" }}>
                      {lists ? (
                        <>
                          <div className="table-responsive table-scroll-fixed">
                            <table className="table table-striped table-hover  table_client">
                              <thead>
                                <tr
                                  className="bg-primary"
                                  style={{
                                    color: "white",
                                    borderRadius: "10px !important",
                                  }}
                                >
                                  <th scope="col">Sr.No.</th>
                                  <th
                                    scope="col"
                                    style={{
                                      minWidth: "120px",
                                      textAlign: "left",
                                    }}
                                  >
                                    Action
                                  </th>
                                  <th
                                    scope="col"
                                    style={{
                                      minWidth: "130px",
                                      // maxWidth: "100px",
                                      textAlign: "left",
                                    }}
                                  >
                                    Code
                                  </th>
                                  <th
                                    scope="col"
                                    style={{
                                      minWidth: "170px",
                                      // maxWidth: "200px",
                                      textAlign: "left",
                                    }}
                                  >
                                    Name
                                  </th>
                                  <th
                                    scope="col"
                                    style={{
                                      minWidth: "100px",
                                      textAlign: "left",
                                    }}
                                  >
                                    Client Name
                                  </th>
                                  <th scope="col " className="zi">
                                    Country
                                  </th>
                                  <th scope="col">Starts</th>
                                  <th scope="col">C</th>
                                  <th scope="col">T</th>
                                  <th scope="col">Q</th>
                                  <th scope="col">D</th>
                                  <th scope="col">LastComplete</th>
                                  <th scope="col">EPC</th>
                                  <th scope="col">S.Size</th>
                                  <th scope="col">IR(%)</th>
                                  <th scope="col">CR(%)</th>
                                  <th scope="col">DR(%)</th>
                                  <th scope="col">LOI</th>
                                  <th scope="col">CPI</th>
                                  <th scope="col">PS</th>
                                </tr>
                              </thead>
                              <tbody>
                                {lists?.length > 0 ? (
                                  <>
                                    {lists?.map((row, i) => {
                                      return (
                                        <>
                                          <tr>
                                            <td scope="row">{rowOffset + i + 1}</td>
                                            <td style={{ textAlign: "left" }}>
                                              {row.hasChild === true ? (
                                                <>
                                                  {row?.recontactParentID >
                                                  0 ? (
                                                    <>
                                                      <span
                                                        className="p-1 bg-blue-circle"
                                                        title="Recontact"
                                                      >
                                                        {" "}
                                                        R{" "}
                                                      </span>
                                                    </>
                                                  ) : (
                                                    <></>
                                                  )}
                                                  <span className="p-1">
                                                    <i
                                                      className="ri-add-fill pointerC"
                                                      onClick={() =>
                                                        navigate(
                                                          "/group-project-child-add",
                                                          {
                                                            state:
                                                              row.projectID,
                                                          }
                                                        )
                                                      }
                                                    ></i>
                                                  </span>
                                                  <span className="p-1">
                                                    <i
                                                      className="ri-edit-2-line pointerC"
                                                      onClick={() =>
                                                        navigate(
                                                          "/group-project-edit",
                                                          {
                                                            state:
                                                              row?.projectID,
                                                          }
                                                        )
                                                      }
                                                    ></i>
                                                  </span>
                                                  {/* <span className="p-1"><i className="ri-delete-bin-2-line del pointerC" onClick={() => deleteRow(row?.projectID, 'group')}></i></span> */}
                                                  {row?.childProjects?.length >
                                              0 &&
                                                  <span className="p-1">
                                                    <i
                                                      className="ri-arrow-right-down-line pointerC"
                                                      id={
                                                        "child" + row?.projectID
                                                      }
                                                      onClick={() =>
                                                        expdRow(row?.projectID)
                                                      }
                                                    ></i>
                                                  </span>
                                              }
                                                </>
                                              ) : (
                                                <>
                                                  {row?.notes ? (
                                                    <>
                                                      <span className="p-1 bg-blue-circle">
                                                        <i className="ri-file-text-line"></i>
                                                      </span>
                                                    </>
                                                  ) : (
                                                    <></>
                                                  )}
                                                  {row?.isSecurityEnabled ? (
                                                    <>
                                                      <span className="p-1 bg-blue-circle">
                                                        {" "}
                                                        S{" "}
                                                      </span>
                                                    </>
                                                  ) : (
                                                    <></>
                                                  )}
                                                  {row?.recontactParentID >
                                                  0 ? (
                                                    <>
                                                      <span
                                                        className="p-1 bg-blue-circle"
                                                        title="Recontact"
                                                      >
                                                        {" "}
                                                        R{" "}
                                                      </span>
                                                    </>
                                                  ) : (
                                                    <></>
                                                  )}
                                                  {/* <span className="p-1"><i className="ri-delete-bin-2-line del pointerC" onClick={() => deleteRow(row?.projectDetailID, 'single')}></i></span> */}
                                                </>
                                              )}
                                            </td>
                                            <td style={{ textAlign: "left" }}>
                                              {row?.childProjects.length > 0 ? (
                                                <>
                                                  <NavLink
                                                    onClick={() =>
                                                      projectDetailGroup(
                                                        row?.projectID
                                                      )
                                                    }
                                                  >
                                                    {row?.currentStatus ===
                                                    "active" ? (
                                                      <>
                                                        <span className="badge bg-success active_btn">
                                                          a
                                                        </span>{" "}
                                                      </>
                                                    ) : (
                                                      <>
                                                        {row?.currentStatus ===
                                                        "inactive" ? (
                                                          <>
                                                            <span className="badge bg-warning inactive_btn">
                                                              a
                                                            </span>
                                                          </>
                                                        ) : (
                                                          <>
                                                            {row?.currentStatus ===
                                                            "invoiced" ? (
                                                              <>
                                                                <span className="badge Invoiced_btn">
                                                                  a
                                                                </span>
                                                              </>
                                                            ) : (
                                                              <>
                                                                {row?.currentStatus ===
                                                                "closed" ? (
                                                                  <>
                                                                    <span className="badge bg-danger closed_btn">
                                                                      a
                                                                    </span>
                                                                  </>
                                                                ) : (
                                                                  <></>
                                                                )}
                                                              </>
                                                            )}
                                                          </>
                                                        )}
                                                      </>
                                                    )}
                                                    <span className="clickableText">
                                                      {" " + row.projectCode}
                                                    </span>
                                                  </NavLink>
                                                </>
                                              ) : (
                                                <>
                                                  <NavLink
                                                    to={
                                                      "/single-project-detail?id=" +
                                                      row?.projectDetailID
                                                    }
                                                  >
                                                    {row?.currentStatus ===
                                                    "active" ? (
                                                      <>
                                                        <span className="badge bg-success active_btn">
                                                          a
                                                        </span>{" "}
                                                      </>
                                                    ) : (
                                                      <>
                                                        {row?.currentStatus ===
                                                        "inactive" ? (
                                                          <>
                                                            <span className="badge bg-warning inactive_btn">
                                                              a
                                                            </span>
                                                          </>
                                                        ) : (
                                                          <>
                                                            {row?.currentStatus ===
                                                            "invoiced" ? (
                                                              <>
                                                                <span className="badge Invoiced_btn">
                                                                  a
                                                                </span>
                                                              </>
                                                            ) : (
                                                              <>
                                                                {row?.currentStatus ===
                                                                "closed" ? (
                                                                  <>
                                                                    <span className="badge bg-danger closed_btn">
                                                                      a
                                                                    </span>
                                                                  </>
                                                                ) : (
                                                                  <></>
                                                                )}
                                                              </>
                                                            )}
                                                          </>
                                                        )}
                                                      </>
                                                    )}
                                                    <span className="clickableText">
                                                      {" " + row.projectCode}
                                                    </span>
                                                  </NavLink>
                                                </>
                                              )}
                                            </td>
                                            <td
                                              style={{ textAlign: "left" }}
                                              onMouseEnter={() =>
                                                handleMouseEnter(i)
                                              }
                                              onMouseLeave={() =>
                                                handleMouseLeave(-1)
                                              }
                                            >
                                              {i === hoveredRow ||
                                              row?.fullName.length <= 20
                                                ? row?.fullName
                                                : row?.fullName.slice(0, 20) +
                                                  "..."}
                                            </td>
                                            <td style={{ textAlign: "left" }}>
                                              {row?.clientName}
                                            </td>
                                            <td>{row?.countryName}</td>
                                            <td>{row?.starts}</td>
                                            <td>{row?.c}</td>
                                            <td>{row?.t}</td>
                                            <td>{row?.q}</td>
                                            <td>{row?.d}</td>
                                            <td>
                                              {row?.lastComplete === 0 ? (
                                                <>-</>
                                              ) : (
                                                <>
                                                  {moment(
                                                    row?.lastComplete * 1000
                                                  ).format("DD-MM-yyyy")}
                                                </>
                                              )}
                                            </td>
                                            <td>{row?.epc}</td>
                                            <td>{row?.sampleSize}</td>
                                            <td>{row?.ir}</td>
                                            <td>{row?.cr}</td>
                                            <td>{row?.dr}</td>
                                            <td>{row?.loi}</td>
                                            <td>{row?.cpi}</td>
                                            <td>
                                              <div className="form-check ">
                                                <input
                                                  className="form-check-input"
                                                  type="checkbox"
                                                  id="formCheck1"
                                                  {...(row?.preScreen
                                                    ? { checked: true }
                                                    : { checked: false })}
                                                />
                                              </div>
                                            </td>
                                          </tr>
                                          {childRow &&
                                          parentId &&
                                          row?.projectID === parentId ? (
                                            <>
                                              {row?.childProjects.length > 0 ? (
                                                <>
                                                  {row?.childProjects?.map(
                                                    (child) => {
                                                      return (
                                                        <>
                                                          <tr>
                                                            <td></td>
                                                            <td>
                                                              {child?.notes ? (
                                                                <>
                                                                  <span className="p-1 bg-blue-circle">
                                                                    <i className="ri-file-text-line"></i>
                                                                  </span>
                                                                </>
                                                              ) : (
                                                                <></>
                                                              )}
                                                              {child?.isSecurityEnabled ? (
                                                                <>
                                                                  <span className="p-1 bg-blue-circle">
                                                                    {" "}
                                                                    S{" "}
                                                                  </span>
                                                                </>
                                                              ) : (
                                                                <></>
                                                              )}
                                                              {child?.recontactParentID >
                                                              0 ? (
                                                                <>
                                                                  <span
                                                                    className="p-1 bg-blue-circle"
                                                                    title="Recontact"
                                                                  >
                                                                    {" "}
                                                                    R{" "}
                                                                  </span>
                                                                </>
                                                              ) : (
                                                                <></>
                                                              )}
                                                            </td>
                                                            <td
                                                              style={{
                                                                textAlign:
                                                                  "left",
                                                              }}
                                                            >
                                                              {/* <span className="clickableText" onClick={() => navigate('/group-project-child-detail?id=' + child?.projectDetailID, { state: child?.projectDetailID })}>{child?.projectCode}</span> */}
                                                              <NavLink
                                                                className="clickableText"
                                                                to={
                                                                  "/group-project-child-detail?id=" +
                                                                  child?.projectDetailID
                                                                }
                                                              >
                                                                 {child?.currentStatus === "active" ? (
                                                                    <>
                                                                        <span className="badge bg-success active_btn">
                                                                          a
                                                                        </span>{" "}
                                                                      </>
                                                                    ) : (
                                                                    <>
                                                                      {child?.currentStatus ===
                                                                      "inactive" ? (
                                                                        <>
                                                                          <span className="badge bg-warning inactive_btn">
                                                                            a
                                                                          </span>
                                                                        </>
                                                                      ) : (
                                                                        <>
                                                                          {child?.currentStatus ===
                                                                          "invoiced" ? (
                                                                            <>
                                                                              <span className="badge Invoiced_btn">
                                                                                a
                                                                              </span>
                                                                            </>
                                                                          ) : (
                                                                            <>
                                                                              {child?.currentStatus ===
                                                                              "closed" ? (
                                                                                <>
                                                                                  <span className="badge bg-danger closed_btn">
                                                                                    a
                                                                                  </span>
                                                                                </>
                                                                              ) : (
                                                                                <></>
                                                                              )}
                                                                            </>
                                                                          )}
                                                                        </>
                                                                      )}
                                                                    </>
                                                                  )} &nbsp;
                                                                {
                                                                  child?.projectCode
                                                                }
                                                              </NavLink>
                                                            </td>
                                                            <td
                                                              style={{
                                                                textAlign:
                                                                  "left",
                                                              }}
                                                            >
                                                              {child?.fullName}
                                                            </td>
                                                            <td
                                                              style={{
                                                                textAlign:
                                                                  "left",
                                                              }}
                                                            >
                                                              {
                                                                child?.clientName
                                                              }
                                                            </td>
                                                            <td>
                                                              {
                                                                child?.countryName
                                                              }
                                                            </td>
                                                            <td>
                                                              {child?.starts}
                                                            </td>
                                                            <td>{child?.c}</td>
                                                            <td>{child?.t}</td>
                                                            <td>{child?.q}</td>
                                                            <td>{child?.d}</td>
                                                            <td>
                                                              {child?.lastComplete ===
                                                              0 ? (
                                                                <>-</>
                                                              ) : (
                                                                <>
                                                                  {moment(
                                                                    child?.lastComplete *
                                                                      1000
                                                                  ).format(
                                                                    "DD-MM-yyyy"
                                                                  )}
                                                                </>
                                                              )}
                                                            </td>
                                                            <td>
                                                              {child?.epc}
                                                            </td>
                                                            <td>
                                                              {
                                                                child?.sampleSize
                                                              }
                                                            </td>
                                                            <td>{child?.ir}</td>
                                                            <td>{child?.cr}</td>
                                                            <td>{child?.dr}</td>
                                                            <td>
                                                              {child?.loi}
                                                            </td>
                                                            <td>
                                                              {child?.cpi}
                                                            </td>
                                                            <td>
                                                              <div className="form-check ">
                                                                <input
                                                                  className="form-check-input"
                                                                  type="checkbox"
                                                                  id="formCheck1"
                                                                />
                                                              </div>
                                                            </td>
                                                          </tr>
                                                        </>
                                                      );
                                                    }
                                                  )}
                                                </>
                                              ) : (
                                                <></>
                                              )}
                                            </>
                                          ) : (
                                            <></>
                                          )}
                                        </>
                                      );
                                    })}
                                  </>
                                ) : (
                                  <>
                                    <tr>
                                      <td colSpan={13}>
                                        <center>No data found</center>
                                      </td>
                                    </tr>
                                  </>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </>
                      ) : (
                        <>
                          <Preloader />
                        </>
                      )}

                      <Pagination pageInfo={pageInfo} changePage={changePage} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
      {visibleDetail ? (
        <>
          <>
            {/* <CButton onClick={() => setVisibleDetail(!visibleDetail)}>Launch static backdrop modal</CButton> */}
            <CModal
              visible={visibleDetail}
              backdrop={"static"}
              size={"xl"}
              onClose={() => setVisibleDetail(false)}
            >
              <CModalHeader
                style={{
                  backgroundColor: "#005499",
                  paddingBottom: "10px",
                  margin: "-1px",
                }}
                closeButton={false}
              >
                <CModalTitle style={{ color: "#fff", width: "100%" }}>
                  Group Project Details
                  <span
                    style={{ float: "right", cursor: "pointer" }}
                    onClick={() => setVisibleDetail(false)}
                  >
                    {" "}
                    X{" "}
                  </span>
                </CModalTitle>
              </CModalHeader>
              <CModalBody>
                <div className="row mt-2">
                  <div className="col-xl-12 col-lg-12">
                    <table className="GroupProjectDetailTable">
                      <tr>
                        <th>
                          Client Name <span className="colonSeperator">:</span>
                        </th>
                        <td>{detail?.clientName}</td>
                      </tr>
                      <tr>
                        <th>
                          Project Code <span className="colonSeperator">:</span>
                        </th>
                        <td>{detail?.projectCode}</td>
                      </tr>
                      <tr>
                        <th>
                          Project Name <span className="colonSeperator">:</span>{" "}
                        </th>
                        <td>{detail?.fullName}</td>
                      </tr>
                      <tr>
                        <th>
                          Project Description{" "}
                          <span className="colonSeperator">:</span>{" "}
                        </th>
                        <td>{detail?.description}</td>
                      </tr>
                      {detail?.dynamicThanksEnabled && (
                        <tr>
                          <th>
                            Dynamic Thanks URL{" "}
                            <span className="colonSeperator">:</span>
                          </th>
                          <td>
                            <input
                              type="checkbox"
                              id="frmdynamicThanksEnabled"
                              defaultChecked={detail?.dynamicThanksEnabled}
                              data-switch="success"
                              disabled
                              style={{
                                backgroundColor: "#000",
                                borderColor: "#000",
                              }}
                            />
                          </td>
                        </tr>
                      )}
                    </table>
                  </div>
                </div>
                <div className="form_divide">
                  <hr></hr>
                </div>
                <div className="row mt-2">
                  <div className="col-xl-12 col-lg-12">
                    <h3>Redirect Link for Client</h3>
                  </div>
                  <div className="col-xl-12 col-lg-12">
                    <table className="GroupProjectDetailTable">
                      {redirectLinks?.map((rl, i) => {
                        return (
                          <>
                            <tr>
                              <th>
                                {rl?.label}{" "}
                                <span className="colonSeperator">:</span>{" "}
                              </th>
                              <td>{rl?.link}</td>
                            </tr>
                          </>
                        );
                      })}
                    </table>
                  </div>
                </div>
                <div className="form_divide">
                  <hr></hr>
                </div>
                <div className="row mt-2">
                  <div className="col-xl-12 col-lg-12">
                    <h3>Notes</h3>
                  </div>
                  <div className="col-xl-12 col-lg-12"></div>
                </div>
              </CModalBody>
            </CModal>
          </>
        </>
      ) : (
        <></>
      )}
    </div>
  );
}

export default All_project;
